import { HttpMethods } from '@codametrix/ui-common';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../core/net';
import {
  canonicalServiceLine,
  ConfigurationValueLike,
  resolveConfigurationValue
} from '../core/resolve-configuration-value';

export const VERTICAL_NAV_CONFIGURATION_ID = 'vertical-view-selector';

/** Mirrors the remote's bounded retry (packages/service-desk/.../Worklist.tsx). */
export const VERTICAL_NAV_MAX_ATTEMPTS = 3;
export const VERTICAL_NAV_RETRY_DELAY_MS = 1000;

interface FetchVerticalNavFlagPayload {
  tenantId: string;
  serviceLine: string;
}

export interface VerticalNavFlagResult {
  tenantId: string;
  serviceLine: string;
  /** null means the read never resolved — indeterminate, NOT "off". */
  isEnabled: boolean | null;
}

/** What the service-desk worklist widget resolved for the same configuration key. */
export interface RemoteVerticalNavReport {
  tenantId?: string;
  serviceLine?: string;
  isEnabled: boolean | null;
  /** Which widget mount produced the report — see `apps/service-desk/service-desk.tsx`. */
  generation?: number;
}

/** Dispatched from `widgetOptions.onVerticalNavResolved`. See `selectHidePrioritizeNav`. */
export const receiveRemoteVerticalNav = createAction<RemoteVerticalNavReport>(
  'RECEIVE_REMOTE_VERTICAL_NAV'
);

const delay = (ms: number) =>
  new Promise<void>(resolve => setTimeout(resolve, ms));

/**
 * The result carries the tenant and service line it answered for, so the reducer can drop a
 * stale or out-of-order response instead of applying it to the current context.
 */
export const fetchVerticalNavFlag = createAsyncThunk(
  'FETCH_VERTICAL_NAV_FLAG',
  async ({
    tenantId,
    serviceLine
  }: FetchVerticalNavFlagPayload): Promise<VerticalNavFlagResult> => {
    const effectiveServiceLine = canonicalServiceLine(serviceLine);
    const endpoint = `/configuration/detail/${VERTICAL_NAV_CONFIGURATION_ID}/${tenantId}/v2?merge=false&serviceLine=${effectiveServiceLine}`;

    for (let attempt = 1; attempt <= VERTICAL_NAV_MAX_ATTEMPTS; attempt += 1) {
      try {
        const response = await api<any>({
          endpoint,
          init: { method: HttpMethods.GET }
        });

        // `api` resolves undefined for the non-2xx statuses it does not throw on (429, 422, …).
        // The remote treats every non-ok response as unavailable and retries, so this side must
        // too, or the two readers disagree on the same failure.
        if (response === undefined || response === null) {
          throw new Error('empty configuration response');
        }

        // The remote reads the array envelope and no other shape, and answers "nothing matched"
        // for anything else. Accepting a bare object here would resolve a value it resolves as
        // null, so the same body would produce opposite answers on the two sides.
        const configuration = Array.isArray(response) ? response[0] : undefined;
        const values = (
          Array.isArray(configuration?.values) ? configuration.values : []
        ) as ConfigurationValueLike[];
        const resolved = resolveConfigurationValue(values, tenantId, serviceLine);

        // "No row matches" is an answer, not a failure, so it is not retried.
        return { tenantId, serviceLine, isEnabled: resolved === 'true' };
      } catch (error) {
        console.warn(
          `[fetchVerticalNavFlag] read failed (attempt ${attempt}/${VERTICAL_NAV_MAX_ATTEMPTS}) for tenantId=${tenantId}, serviceLine=${effectiveServiceLine}`,
          error
        );
        if (attempt < VERTICAL_NAV_MAX_ATTEMPTS) {
          await delay(VERTICAL_NAV_RETRY_DELAY_MS);
        }
      }
    }

    return { tenantId, serviceLine, isEnabled: null };
  }
);
